/**
 * OL-3 location triad for Ops Lookup.
 * Plant / dealer / customer slots; current = one of three.
 */

import type { OpsLookupLocationKind, OpsLookupUnitRow } from "@/lib/opsLookup/opsLookupModel";
import { inferCurrentLocation } from "@/lib/opsLookup/opsLookupModel";

export type OpsLookupLocationSlot = {
  kind: OpsLookupLocationKind;
  label: string;
  detail: string;
  isCurrent: boolean;
  source: "plant" | "unit_field" | "route_stop" | "empty";
};

export type OpsLookupStopLike = {
  stop_kind: string;
  address?: Record<string, unknown> | null;
  arrived_at?: string | null;
  departed_at?: string | null;
  sequence_index?: number;
};

export function formatLogiAddress(address: Record<string, unknown> | null | undefined): string {
  if (!address || typeof address !== "object") return "";
  const line1 = String(address.line1 ?? address.street ?? address.address1 ?? "").trim();
  const city = String(address.city ?? "").trim();
  const state = String(address.state ?? address.region ?? "").trim();
  const zip = String(address.postal_code ?? address.zip ?? "").trim();
  const parts = [line1, [city, state].filter(Boolean).join(", "), zip].filter(Boolean);
  if (parts.length) return parts.join(" · ");
  const fallback = String(address.formatted ?? address.label ?? "").trim();
  return fallback;
}

function stopKindToLocation(kind: string): OpsLookupLocationKind | null {
  if (kind === "plant_origin") return "plant";
  if (kind === "dealer_holding") return "dealer";
  if (kind === "end_customer") return "customer";
  return null;
}

/** Prefer last stop with arrival; else delivered/staged heuristics on the unit. */
export function inferCurrentLocationFromStops(
  unit: OpsLookupUnitRow,
  stops: OpsLookupStopLike[],
): OpsLookupLocationKind {
  const ordered = [...stops].sort(
    (a, b) => (a.sequence_index ?? 0) - (b.sequence_index ?? 0),
  );
  for (let i = ordered.length - 1; i >= 0; i--) {
    const s = ordered[i];
    if (!s.arrived_at) continue;
    const mapped = stopKindToLocation(s.stop_kind);
    if (mapped) return mapped;
  }
  return inferCurrentLocation(unit);
}

export function buildOpsLookupLocationTriad(input: {
  unit: OpsLookupUnitRow;
  plantName?: string | null;
  plantNotes?: string | null;
  stops?: OpsLookupStopLike[] | null;
}): OpsLookupLocationSlot[] {
  const { unit, plantName, plantNotes, stops } = input;
  const stopList = stops ?? [];
  const current = inferCurrentLocationFromStops(unit, stopList);

  const byKind: Record<OpsLookupLocationKind, { detail: string; source: OpsLookupLocationSlot["source"] }> = {
    plant: {
      detail: [plantName || null, plantNotes || null].filter(Boolean).join(" — ") || unit.plant_id || "—",
      source: plantName || plantNotes ? "plant" : unit.plant_id ? "unit_field" : "empty",
    },
    dealer: {
      detail: unit.seller_location_name || unit.inventory_holder_org_id || "—",
      source: unit.seller_location_name || unit.inventory_holder_org_id ? "unit_field" : "empty",
    },
    customer: {
      detail: unit.customer_name || unit.end_customer_contact_id || "—",
      source: unit.customer_name || unit.end_customer_contact_id ? "unit_field" : "empty",
    },
  };

  for (const stop of stopList) {
    const kind = stopKindToLocation(stop.stop_kind);
    if (!kind) continue;
    const addr = formatLogiAddress(stop.address ?? null);
    if (!addr) continue;
    byKind[kind] = { detail: addr, source: "route_stop" };
  }

  const labels: Record<OpsLookupLocationKind, string> = {
    plant: "Plant",
    dealer: "Dealer",
    customer: "Customer",
  };

  return (["plant", "dealer", "customer"] as OpsLookupLocationKind[]).map((kind) => ({
    kind,
    label: labels[kind],
    detail: byKind[kind].detail || "—",
    isCurrent: current === kind,
    source: byKind[kind].source,
  }));
}
